import { Material } from '@/app/entities/Material'
import { useMaterialMutations } from '@/app/hooks/useMaterialMutations'

import { Button } from '@/view/components/ui/button'
import { DatePicker } from '@/view/components/ui/date-picker'
import { DialogContent } from '@/view/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/view/components/ui/form'
import { Input } from '@/view/components/ui/input'
import { Separator } from '@/view/components/ui/separator'
import { Switch } from '@/view/components/ui/switch'
import { InputMask } from '@react-input/mask'
import { useMaterialForm } from '../../hooks/useMaterialForm'
import { useCreateMaterialDialog } from '../../providers/CreateMaterialDialogProvider'
import { MATERIAL_RESOLUTIONS, parseMaterialToFormValues } from '../helper'
import { Textarea } from '@/view/components/ui/textarea'
import { MaterialAccordion } from './MaterialAccordion'
interface MaterialFormProps {
  material?: Material
  onSucess?: () => void
}

export const MaterialForm = ({ material, onSucess }: MaterialFormProps) => {
  const { setIsCreateModalOpen } = useCreateMaterialDialog()

  const form = useMaterialForm({
    getDefaultValues: material
      ? () => parseMaterialToFormValues(material)
      : undefined,
  })

  const {
    createMaterial,
    updateMaterial,
    deleteMaterialContent,
    isDeletingMaterialContent,
    isCreating,
    isUpdating,
  } = useMaterialMutations()

  const isEdit = !!material

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      if (isEdit) {
        await updateMaterial({
          materialId: material?._id || '',
          material: values,
          oldMaterial: material,
        })
        onSucess?.()
      } else {
        await createMaterial(values)
        setIsCreateModalOpen(false)
      }
    } catch (error) {
      console.error('Erro ao salvar material:', error)
    }
  })

  const handleDeleteMaterialContent = async (file: File) => {
    if (!isEdit) return
    await deleteMaterialContent({
      materialId: material?._id || '',
      path: file.name,
    })
  }

  return (
    <DialogContent className="flex w-[470px] flex-col gap-0 rounded-3xl">
      <h1 className="mb-2 text-2xl font-semibold">
        {isEdit ? 'Editar Postagem' : 'Nova Postagem'}
      </h1>
      <Separator />
      <Form {...form}>
        <form className="mt-8 space-y-4" onSubmit={handleSubmit}>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título</FormLabel>
                <FormControl>
                  <Input placeholder="Titulo da postagem" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Descrição</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Escreva uma legenda para acompanhar sua postagem"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <div className="flex justify-end">
                  <span className="text-sm text-muted-foreground">
                    {field.value?.length || 0}/500
                  </span>
                </div>
              </FormItem>
            )}
          />
          <div className="flex w-full justify-between gap-4">
            <FormField
              control={form.control}
              name="liberationDate"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Data de Liberação</FormLabel>
                  <FormControl>
                    <DatePicker
                      onChange={field.onChange}
                      selected={field.value}
                      label="Data de liberação"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="liberationTime"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>Horário de Liberação</FormLabel>
                  <FormControl>
                    <InputMask
                      mask="xx:xx"
                      replacement={{ x: /[0-9]/ }}
                      component={Input}
                      placeholder="00:00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div>
            <h2 className="font-semibold">Conteúdos</h2>
            <p className="text-sm text-muted-foreground">
              Adicione suas mídias abaixo {'–'} imagens e vídeos para feed e
              stories
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {MATERIAL_RESOLUTIONS.map((material) => (
              <MaterialAccordion
                key={material.type}
                form={form}
                isDeletingMaterial={isDeletingMaterialContent}
                onDeleteMaterial={handleDeleteMaterialContent}
                {...material}
              />
            ))}
          </div>

          <FormField
            control={form.control}
            name="isSellersApp"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Vendedores</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="isTeamsApp"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Líderes</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Button
            disabled={isCreating || isUpdating}
            className="w-full rounded-full text-white"
            type="submit"
          >
            Confirmar
          </Button>
        </form>
      </Form>
    </DialogContent>
  )
}
